import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Flex,
  Grid,
  Progress,
  Skeleton,
  SkeletonText,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { lazy, Suspense, useCallback, useContext, useEffect, useState } from "react";
import { subDays, format } from "date-fns";
import { MdKeyboardVoice, MdPeople, MdTextFields, MdTimeline } from "react-icons/md";

import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";
import { AgencyContext } from "../../Settings/AgencyProvider/AgencyProvider";
import CustomDateRangePicker from "../../../utils/CustomDateRange/CustomDateRangePicker";

const Bar = lazy(() =>
  import("react-chartjs-2").then((m) => {
    const { Chart, registerables } = require("chart.js");
    Chart.register(...registerables);
    return { default: m.Bar };
  })
);

function ChartSpinner() {
  return (
    <Flex justify="center" align="center" h="200px">
      <Spinner size="sm" color="gray.300" />
    </Flex>
  );
}

function OverviewCard({ title, value, sub, icon: Icon, isLoading }) {
  const { cardBg, textColor } = useContext(AgencyContext);
  return (
    <Card variant="elevated" bg={cardBg}>
      <CardBody p={4}>
        <Flex align="center" gap={2} mb={2}>
          {Icon && <Icon size={16} color="#9CA3AF" />}
          <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
            {title}
          </Text>
        </Flex>
        <Skeleton isLoaded={!isLoading} fitContent>
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>{value ?? "—"}</Text>
        </Skeleton>
        {sub && <Text fontSize="xs" color="gray.400" mt={1}>{sub}</Text>}
      </CardBody>
    </Card>
  );
}

function UsageBar({ label, used, allocated, unit = "" }) {
  const pct = allocated ? Math.min(100, Math.round((used / allocated) * 100)) : 0;
  const scheme = pct > 85 ? "red" : pct > 60 ? "yellow" : "blue";
  return (
    <Box>
      <Flex justify="space-between" mb={1}>
        <Text fontSize="xs" color="gray.600">{label}</Text>
        <Text fontSize="xs" color="gray.500">
          {used?.toLocaleString()} / {allocated?.toLocaleString()}{unit}
        </Text>
      </Flex>
      <Tooltip label={`${pct}% used`} placement="top">
        <Progress value={pct} size="sm" colorScheme={scheme} borderRadius="full" />
      </Tooltip>
    </Box>
  );
}

export default function AgencyAnalytics() {
  const baseUrl = getHostDomain();
  const { agencyData, textColor, buttonColorScheme } = useContext(AgencyContext);
  const agency_id = agencyData?.id;

  const [overview, setOverview] = useState(null);
  const [overviewStatus, setOverviewStatus] = useState("loading");

  const [granularity, setGranularity] = useState("daily");
  const [dateRange, setDateRange] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
  });
  const [timeseries, setTimeseries] = useState([]);
  const [tsStatus, setTsStatus] = useState("loading");

  const [subaccounts, setSubaccounts] = useState([]);
  const [saTotal, setSaTotal] = useState(0);
  const [saPage, setSaPage] = useState(1);
  const [saStatus, setSaStatus] = useState("loading");
  const SA_SIZE = 25;

  // Overview — fetched once on mount
  useEffect(() => {
    if (!agency_id) return;
    (async () => {
      setOverviewStatus("loading");
      try {
        const res = await fetchController(`${baseUrl}/api/v1/agency/analytics/${agency_id}/overview`, "GET");
        setOverview(res);
      } catch {
        setOverview(null);
      } finally {
        setOverviewStatus("idle");
      }
    })();
  }, [agency_id, baseUrl]);

  // Timeseries — re-fetches on granularity or dateRange change
  const fetchTimeseries = useCallback(async () => {
    if (!agency_id) return;
    setTsStatus("loading");
    try {
      const from = format(dateRange.startDate, "yyyy-MM-dd");
      const to = format(dateRange.endDate, "yyyy-MM-dd");
      const res = await fetchController(
        `${baseUrl}/api/v1/agency/analytics/${agency_id}/timeseries?granularity=${granularity}&date_from=${from}&date_to=${to}`,
        "GET"
      );
      setTimeseries(Array.isArray(res) ? res : []);
    } catch {
      setTimeseries([]);
    } finally {
      setTsStatus("idle");
    }
  }, [agency_id, baseUrl, granularity, dateRange.startDate, dateRange.endDate]);

  useEffect(() => { fetchTimeseries(); }, [fetchTimeseries]);

  // Subaccounts table — re-fetches on page change
  const fetchSubaccounts = useCallback(async () => {
    if (!agency_id) return;
    setSaStatus("loading");
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/agency/analytics/${agency_id}/subaccounts?page=${saPage}&size=${SA_SIZE}`,
        "GET"
      );
      setSubaccounts(res?.items || []);
      setSaTotal(res?.total || 0);
    } catch {
      setSubaccounts([]);
    } finally {
      setSaStatus("idle");
    }
  }, [agency_id, baseUrl, saPage]);

  useEffect(() => { fetchSubaccounts(); }, [fetchSubaccounts]);

  // Chart config for timeseries
  const tsLabels = timeseries.map((d) => d.period);
  const tsConfig = {
    data: {
      labels: tsLabels,
      datasets: [
        {
          label: "Text Credits",
          data: timeseries.map((d) => d.utilized_text_credits),
          backgroundColor: "#4F46E5",
          stack: "usage",
          borderRadius: 3,
        },
        {
          label: "Voice Minutes",
          data: timeseries.map((d) => Math.round(d.call_duration / 60)),
          backgroundColor: "#7C3AED",
          stack: "usage",
          borderRadius: 3,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 }, color: "#6B7280" } },
        tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${c.parsed.y.toLocaleString()}` } },
      },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { color: "#9CA3AF" } },
        y: { stacked: true, beginAtZero: true, grid: { color: "rgba(156,163,175,0.15)" }, ticks: { color: "#9CA3AF", precision: 0 } },
      },
    },
  };

  const totalPages = Math.ceil(saTotal / SA_SIZE);

  return (
    <Flex direction="column" gap={5} py={2}>

      {/* Row 1: Overview cards */}
      <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(4,1fr)" }} gap={4}>
        <OverviewCard
          title="Text Queries (this month)"
          value={(overview?.text_queries_total ?? 0).toLocaleString()}
          icon={MdTextFields}
          isLoading={overviewStatus === "loading"}
        />
        <OverviewCard
          title="Voice Minutes (this month)"
          value={(overview?.voice_minutes_total ?? 0).toLocaleString()}
          sub="minutes used"
          icon={MdKeyboardVoice}
          isLoading={overviewStatus === "loading"}
        />
        <OverviewCard
          title="Voice Credits (this month)"
          value={(overview?.voice_credits_total ?? 0).toLocaleString()}
          icon={MdTimeline}
          isLoading={overviewStatus === "loading"}
        />
        <OverviewCard
          title="Active Subaccounts"
          value={(overview?.active_subaccounts ?? 0).toLocaleString()}
          sub={`of ${saTotal} total`}
          icon={MdPeople}
          isLoading={overviewStatus === "loading"}
        />
      </Grid>

      {/* Row 2: Usage trend chart */}
      <Card variant="elevated">
        <CardBody>
          <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
            <Text fontWeight="semibold" color={textColor}>Usage Trend</Text>
            <Flex gap={2} align="center" flexWrap="wrap">
              <ButtonGroup size="xs" isAttached variant="outline">
                <Button
                  colorScheme={granularity === "daily" ? buttonColorScheme : "gray"}
                  variant={granularity === "daily" ? "solid" : "outline"}
                  onClick={() => setGranularity("daily")}
                >
                  Daily
                </Button>
                <Button
                  colorScheme={granularity === "monthly" ? buttonColorScheme : "gray"}
                  variant={granularity === "monthly" ? "solid" : "outline"}
                  onClick={() => setGranularity("monthly")}
                >
                  Monthly
                </Button>
              </ButtonGroup>
              <CustomDateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={({ startDate, endDate }) => setDateRange({ startDate, endDate })}
              />
            </Flex>
          </Flex>
          {tsStatus === "loading" ? (
            <ChartSpinner />
          ) : !timeseries.length ? (
            <Flex justify="center" align="center" h="200px">
              <Text fontSize="sm" color="gray.400">No usage data for this period</Text>
            </Flex>
          ) : (
            <Suspense fallback={<ChartSpinner />}>
              <div style={{ height: "240px", position: "relative" }}>
                <Bar {...tsConfig} />
              </div>
            </Suspense>
          )}
        </CardBody>
      </Card>

      {/* Row 3: Subaccount table */}
      <Card variant="elevated">
        <CardBody>
          <Text fontWeight="semibold" color={textColor} mb={4}>
            Subaccount Usage Breakdown
          </Text>
          {saStatus === "loading" ? (
            <Flex justify="center" py={8}><Spinner size="sm" /></Flex>
          ) : !subaccounts.length ? (
            <Text fontSize="sm" color="gray.400" textAlign="center" py={8}>No subaccounts found</Text>
          ) : (
            <>
              <Box overflowX="auto">
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr>
                      {["Status", "Name / Email", "Plan", "Text Used / Alloc.", "Voice Used / Alloc.", "This Month Text", "This Month Voice"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {subaccounts.map((sa) => {
                      const textPct = sa.text_allocated ? Math.round((sa.text_used / sa.text_allocated) * 100) : 0;
                      const voicePct = sa.voice_allocated_seconds ? Math.round((sa.voice_used_seconds / sa.voice_allocated_seconds) * 100) : 0;
                      return (
                        <tr key={sa.user_id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                          <td style={{ padding: "10px 12px" }}>
                            <Badge colorScheme={sa.is_active ? "green" : "gray"} fontSize="xs">
                              {sa.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <Text fontSize="sm" fontWeight="medium">{sa.name || "—"}</Text>
                            <Text fontSize="xs" color="gray.400">{sa.email}</Text>
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <Text fontSize="sm">{sa.plan_name || "—"}</Text>
                          </td>
                          <td style={{ padding: "10px 12px", minWidth: "140px" }}>
                            <UsageBar
                              label=""
                              used={sa.text_used}
                              allocated={sa.text_allocated}
                            />
                            <Text fontSize="xs" color="gray.400" mt={0.5}>{textPct}%</Text>
                          </td>
                          <td style={{ padding: "10px 12px", minWidth: "140px" }}>
                            <UsageBar
                              label=""
                              used={Math.round(sa.voice_used_seconds / 60)}
                              allocated={Math.round(sa.voice_allocated_seconds / 60)}
                              unit=" min"
                            />
                            <Text fontSize="xs" color="gray.400" mt={0.5}>{voicePct}%</Text>
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <Text fontSize="sm">{(sa.this_month_text ?? 0).toLocaleString()}</Text>
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <Text fontSize="sm">{sa.this_month_voice_mins ?? 0} min</Text>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
              {totalPages > 1 && (
                <Flex justify="flex-end" align="center" gap={2} mt={3}>
                  <Button size="xs" variant="outline" isDisabled={saPage <= 1} onClick={() => setSaPage((p) => p - 1)}>← Prev</Button>
                  <Text fontSize="xs" color="gray.500">Page {saPage} of {totalPages}</Text>
                  <Button size="xs" variant="outline" isDisabled={saPage >= totalPages} onClick={() => setSaPage((p) => p + 1)}>Next →</Button>
                </Flex>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </Flex>
  );
}
